import { Checkbox, FormControlLabel, MenuItem, Radio, RadioGroup, Switch, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { UiComponent } from '../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import VyMultiSelect from '../../../common/VyMultiSelect';
import VySelect from '../../../common/VySelect';
import VyTooltip from '../../../common/VyTooltip';
import { TabPanel } from '../SidebarToolbar';
import StylingSettingsTemplate from './StylingSettingsTable';

interface SettingsTemplateInterface<T extends UiComponent> {
  onClose?: () => void;
  onChange: (c: T) => void;
  component: T;
  children?: any;
  selectedTab: number;
  StylingChildren: any;
  hasDefaultPadding?: boolean;
}

function SettingsTemplate<T extends UiComponent>({
  component,
  onChange,
  children,
  selectedTab,
  StylingChildren,
  hasDefaultPadding = true,
}: SettingsTemplateInterface<T>) {
  const fields = useSelector(({ common }) => common.registrationFields.filter((f) => f.valueOptions.length > 0 && !f.canMultiSelect), shallowEqual);
  const event = useSelector(({ common }) => common.event);
  const [currComponent, setCurrComponent] = useState(component);

  const { t } = useTranslation();
  useEffect(() => {
    setCurrComponent(component);
  }, [component]);

  useEffect(() => {
    if (currComponent.accessType !== 'visitor') return;
    onChange({ ...currComponent, isRestricted: false });
  }, [currComponent.accessType]);

  /**
   * @name onApprovalChange
   * @param approval type of user approval
   * @description this updates the component state with the chosen approval type for visibility settings
   */
  const onApprovalChange = ([approval]: UiComponent['userApproval']) => {
    let _userApproval = [...currComponent.userApproval];
    const index = _userApproval.indexOf(approval);
    if (index === -1) _userApproval.push(approval);
    else _userApproval.splice(index, 1);

    if (approval === 'all') _userApproval = ['all'];
    else {
      const _i = _userApproval.indexOf('all');
      _i !== -1 && _userApproval.splice(_i, 1);
    }
    if (_userApproval.length === 3) _userApproval = ['all'];
    onChange({ ...currComponent, userApproval: _userApproval });
  };

  return (
    <>
      <>
        <TabPanel value={selectedTab} index={0}>
          <div className={hasDefaultPadding ? 'p-2' : ''}>{children}</div>
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          <StylingSettingsTemplate component={currComponent} onChange={onChange} />
          {StylingChildren}
        </TabPanel>

        <TabPanel value={selectedTab} index={2}>
          <div className="text-black">
            <div className="w-full">
              <Table>
                <TableHead className="bg-slate-300 w-full">
                  <TableRow key="access-type-title">
                    <TableCell>
                      <VyTooltip title={t('pagebuilder:tooltip-access-type')}>
                        <p>{t('pagebuilder:access-type')}</p>
                      </VyTooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key="access-type-title-select" className="w-full">
                    <TableCell style={{ zIndex: 60 }}>
                      <RadioGroup
                        aria-label="access"
                        name="access"
                        value={currComponent.accessType}
                        onChange={(e) => onChange({ ...currComponent, accessType: e.target.value })}>
                        <FormControlLabel value="anyone" control={<Radio />} label={t('pagebuilder:access-anyone')} />
                        <FormControlLabel value="user" control={<Radio />} label={t('pagebuilder:access-user')} />
                        <FormControlLabel value="visitor" control={<Radio />} label={t('pagebuilder:access-visitor')} />
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                  {(currComponent.accessType === 'user' || currComponent.accessType === 'anyone') &&
                    !event?.modules?.guestmanagement?.settings?.autoApproveUser && (
                      <TableRow key="access-type-user-approval" className="w-full">
                        <TableCell style={{ zIndex: 60 }}>
                          <VyTooltip title={t('pagebuilder:tooltip-user-approval')}>
                            <p>{t('pagebuilder:user-approval')}</p>
                          </VyTooltip>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={currComponent.userApproval.includes('all') && currComponent.userApproval.length === 1}
                                onChange={() => onApprovalChange(['all'])}
                                color="primary"
                              />
                            }
                            label={t('pagebuilder:approval-all')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={currComponent.userApproval.includes('pending')}
                                onChange={() => onApprovalChange(['pending'])}
                                color="primary"
                              />
                            }
                            label={t('pagebuilder:approval-pending')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={currComponent.userApproval.includes('accepted')}
                                onChange={() => onApprovalChange(['accepted'])}
                                color="primary"
                              />
                            }
                            label={t('pagebuilder:approval-accepted')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={currComponent.userApproval.includes('rejected')}
                                onChange={() => onApprovalChange(['rejected'])}
                                color="primary"
                              />
                            }
                            label={t('pagebuilder:approval-rejected')}
                          />
                        </TableCell>
                      </TableRow>
                    )}

                  {currComponent.accessType === 'user' && (
                    <TableRow key="access-type-is-restricted" className="w-full">
                      <TableCell style={{ zIndex: 60 }}>
                        <VyTooltip title={t('pagebuilder:tooltip-is-restricted')}>
                          <p>{t('pagebuilder:is-restricted')}</p>
                        </VyTooltip>
                        <Switch
                          checked={fields.length > 0 ? currComponent.isRestricted : false}
                          onChange={(e) => onChange({ ...currComponent, isRestricted: e.target.checked })}
                          disabled={fields.length === 0}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {currComponent.isRestricted && currComponent.accessType === 'user' && (
                    <TableRow key="access-type-profile-field-select" className="w-full">
                      <TableCell style={{ zIndex: 60 }}>
                        <div className="flex flex-col items-center w-full space-x-4">
                          <VyMultiSelect
                            label={t('account:profilefields.profilefields')}
                            name={`profilefields-${component._id}`}
                            options={fields.map((f) => ({ value: f._id, name: f.label }))}
                            selectedOptions={currComponent.registrationFields?.map((f) => ({
                              value: f.field,
                              name: fields.find((rf) => f.field === rf._id)?.label || '',
                            }))}
                            onValueChange={(values) => {
                              const registrationFields = fields
                                .filter((f) => values.includes(f._id))
                                .map((f) => ({
                                  field: f._id,
                                  value: f.textValueOptions[0].content,
                                  ...currComponent.registrationFields.find((rf) => rf.field === f._id),
                                }));
                              onChange({ ...currComponent, registrationFields });
                            }}
                          />
                          <div className="m-4 w-full">
                            {currComponent.registrationFields
                              ?.map((rf) => ({ field: fields.find((f) => f._id === rf.field), value: rf.value }))
                              .map((f, i) => (
                                <VySelect
                                  fullWidth
                                  key={`field-value-${f.field?._id}`}
                                  defaultValue={f.value}
                                  onChange={(e) => {
                                    const _registrationfields = currComponent.registrationFields ? [...currComponent.registrationFields] : [];
                                    _registrationfields[i] = { ..._registrationfields[i], value: e.target.value as string };
                                    onChange({ ...currComponent, registrationFields: _registrationfields });
                                  }}>
                                  {f.field?.valueOptions.map((o) => (
                                    <MenuItem value={o}>{o}</MenuItem>
                                  ))}
                                </VySelect>
                              ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabPanel>
      </>
    </>
  );
}
export default SettingsTemplate;
